import { useEffect, useRef } from 'react'
import { useParams, useNavigate, useBlocker, Link } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { usePermissions } from '@/hooks/usePermissions'
import { useForm, Controller } from 'react-hook-form'
import { ArrowLeft, Eye, EyeOff, Loader2 } from 'lucide-react'
import { Button, Input } from '@brix/ui'
import {
  useBlogPosts, useCreateBlogPost, useUpdateBlogPost,
  type BlogPostInput, type BlogPostRow,
} from '@/hooks/useBlogPosts'
import { RichTextEditor } from '@/components/editor/RichTextEditor'
import { ImageField } from '@/components/editor/fields'

const autoSlug = (s: string) =>
  s.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')

function extractHtml(content: unknown[]): string {
  if (!content?.length) return ''
  const first = content[0] as Record<string, unknown>
  const props = first?.['props'] as Record<string, unknown> | undefined
  return (props?.['content'] as string) ?? ''
}

interface EditorFormProps {
  siteId: string
  post?: BlogPostRow
}

function EditorForm({ siteId, post }: EditorFormProps) {
  const navigate = useNavigate()
  const create = useCreateBlogPost(siteId)
  const update = useUpdateBlogPost(siteId)
  const isPending = create.isPending || update.isPending

  const { register, handleSubmit, setValue, control, watch, formState: { errors, isDirty } } =
    useForm<BlogPostInput>({
      defaultValues: post
        ? {
            title: post.title,
            slug: post.slug,
            excerpt: post.excerpt ?? '',
            content: extractHtml(post.content),
            coverImage: post.coverImage ?? '',
            tags: post.tags.join(', '),
            published: post.published,
          }
        : { title: '', slug: '', excerpt: '', content: '', coverImage: '', tags: '', published: false },
    })

  const published = watch('published')

  // Ref allows onSave to skip the blocker synchronously before calling navigate()
  // (React state updates from reset() batch until after navigate() is already called)
  const skipBlocker = useRef(false)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !skipBlocker.current && isDirty && currentLocation.pathname !== nextLocation.pathname,
  )

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => { if (isDirty) e.preventDefault() }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isDirty])

  const onSave = async (data: BlogPostInput) => {
    if (post) {
      await update.mutateAsync({ id: post.id, input: data })
    } else {
      await create.mutateAsync(data)
    }
    skipBlocker.current = true
    navigate(`/sites/${siteId}/blog`)
  }

  return (
    <form onSubmit={handleSubmit(onSave)} className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 shrink-0">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          {isDirty && (
            <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <label htmlFor="published-toggle" className="flex items-center gap-2 cursor-pointer select-none">
            <input id="published-toggle" type="checkbox" className="sr-only" {...register('published')} />
            <div className={[
              'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              published
                ? 'bg-green-50 text-green-700 hover:bg-green-100'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
            ].join(' ')}>
              {published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              {published ? 'Published' : 'Draft'}
            </div>
          </label>
          <Button type="submit" loading={isPending} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : post ? 'Save changes' : 'Create post'}
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Content area */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          <div className="mx-auto max-w-2xl">
            {/* Cover image */}
            <div className="mb-6">
              <ImageField
                label="Cover image"
                value={watch('coverImage') ?? ''}
                onChange={(url) => setValue('coverImage', url, { shouldDirty: true })}
                folder="blog"
              />
            </div>

            {/* Title */}
            <input
              {...register('title', {
                required: 'Title is required',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!post) setValue('slug', autoSlug(e.target.value))
                },
              })}
              placeholder="Post title…"
              className="mb-1 w-full border-none text-3xl font-bold text-gray-900 placeholder-gray-300 focus:outline-none bg-transparent resize-none"
            />
            {errors.title && (
              <p className="mb-2 text-xs text-red-500">{errors.title.message}</p>
            )}

            {/* Excerpt */}
            <input
              {...register('excerpt')}
              placeholder="Short excerpt shown in listings…"
              className="mb-6 w-full border-none text-base text-gray-500 placeholder-gray-300 focus:outline-none bg-transparent"
            />

            {/* Rich text */}
            <div className="min-h-[400px]">
              <Controller
                name="content"
                control={control}
                render={({ field }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Write your post here…"
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Sidebar — meta fields */}
        <div className="w-64 shrink-0 border-l border-gray-200 bg-white overflow-y-auto px-5 py-6 space-y-5">
          <div>
            <Input
              label="URL slug"
              {...register('slug', { required: 'Required' })}
              error={errors.slug?.message}
            />
          </div>

          <div>
            <Input
              label="Tags"
              {...register('tags')}
              placeholder="tech, design"
              hint="Comma-separated"
            />
          </div>

          {(create.error ?? update.error) && (() => {
            const msg = ((create.error ?? update.error) as Error).message
            const isLimit = msg.toLowerCase().includes('limit') || msg.toLowerCase().includes('upgrade')
            return (
              <p className="text-sm text-red-600">
                {msg}
                {isLimit && (
                  <Link to="/billing" className="ml-1.5 font-semibold underline hover:no-underline">
                    View plans →
                  </Link>
                )}
              </p>
            )
          })()}
        </div>
      </div>

      {blocker.state === 'blocked' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-80 rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900">Unsaved changes</h3>
            <p className="mt-1 text-sm text-gray-500">Your changes will be lost if you leave without saving.</p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => blocker.proceed?.()}
                className="flex-1 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Leave
              </button>
              <button
                type="button"
                onClick={() => blocker.reset?.()}
                className="flex-1 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"
              >
                Stay
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  )
}

// ─── Route wrappers ───────────────────────────────────────────

export function NewBlogPostPage() {
  const { siteId = '' } = useParams()
  return (
    <div className="flex flex-col h-screen">
      <EditorForm siteId={siteId} />
    </div>
  )
}

export function EditBlogPostPage() {
  const { siteId = '', postId = '' } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { isAuthorOnly } = usePermissions()
  const { data: posts = [], isLoading } = useBlogPosts(siteId)
  const post = posts.find((p) => p.id === postId)

  useEffect(() => {
    if (isLoading) return
    if (!post) { navigate(`/sites/${siteId}/blog`, { replace: true }); return }
    if (isAuthorOnly && post.authorId !== user?.id) {
      navigate(`/sites/${siteId}/blog`, { replace: true })
    }
  }, [isLoading, post, siteId, navigate, isAuthorOnly, user?.id])

  if (isLoading || !post) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen">
      <EditorForm siteId={siteId} post={post} />
    </div>
  )
}
